<template>
  <div class="tw:flex tw:flex-col tw:gap-8">
    <section aria-labelledby="event-location-title">
      <h2 id="event-location-title" class="tw:m-0 tw:mb-4 tw:text-base tw:font-semibold tw:text-balance">{{ $t('Country and Time Zone') }}</h2>
      <div class="tw:grid tw:grid-cols-1 tw:gap-4 tw:md:grid-cols-2">
        <div class="tw:min-w-0">
          <label class="tw:mb-2 tw:block tw:text-sm tw:font-medium" for="tour-country">
            {{ $t("Country") }}
          </label>
          <Combobox
            id="tour-country"
            name="country"
            v-model="selectedCountry"
            :options="countryOptions.map(country => ({ value: country.id, label: country.text }))"
            :fullwidth="true"
            required
            />
        </div>

        <div class="tw:min-w-0">
          <label class="tw:mb-2 tw:block tw:text-sm tw:font-medium" for="tour-timezone">
            {{ $t("Timezone") }}
          </label>
          <timezone
                    id="tour-timezone"
                    name="timezone"
                    :value="zone"
                    :timezones="timezones"
                    @timezone-change="onTimezoneChange"
                    required>
          </timezone>
        </div>

      </div>
    </section>
    <section aria-labelledby="event-dates-title">
      <h2 id="event-dates-title" class="tw:m-0 tw:mb-4 tw:text-base tw:font-semibold tw:text-balance">{{ $t('Event Dates') }}</h2>

      <div class="tw:bg-muted/30 tw:border tw:border-border tw:p-4 tw:rounded-lg">
        <div class="tw:flex tw:items-center tw:justify-between tw:gap-2">
          <label for="has-no-dates" class="tw:m-0 tw:flex tw:min-h-11 tw:flex-1 tw:cursor-pointer tw:flex-col tw:justify-center tw:gap-1">
            <span class="tw:text-sm tw:font-medium">
              {{ $t("This event is permanent") }}
            </span>
            <small class="tw:block tw:text-sm tw:leading-relaxed tw:text-pretty tw:text-muted-foreground">{{ $t('If not please specify the dates') }}</small>
          </label>
          <div class="tw:flex tw:min-h-11 tw:min-w-11 tw:shrink-0 tw:items-center tw:justify-center">
            <Switch id="has-no-dates" v-model="hasNoDates"/>
          </div>
        </div>

        <div v-if="!hasNoDates" class="tw:mt-3 tw:pt-3 tw:border-t tw:grid tw:grid-cols-1 tw:gap-4 tw:md:grid-cols-2">
          <div class="tw:min-w-0">
            <label for="frmTourStartDate">{{ $t('Start at:') }}</label>
            <Datetime id="frmTourStartDate"
                      v-model="start"
                      :timezone="zone"
                      :required="!hasNoDates"
                      :disabled="!zone"
                      with-timezone
            ></Datetime>
            <input type="hidden" name="start_at" :value="formatedStartAt">
          </div>

          <div class="tw:min-w-0">
            <label for="frmTourEndDate">{{ $t('End at:') }}</label>
            <Datetime id="frmTourEndDate"
                      v-model="end"
                      :timezone="zone"
                      :required="!hasNoDates"
                      :disabled="!zone"
                      with-timezone
            ></Datetime>
            <input type="hidden" name="end_at" :value="formatedEndAt">
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import Timezone from '@/components/form-components/Timezone.vue';
import Datetime from '@/components/ui/Datetime.vue';
import { Switch } from '@/shadcn/components/ui/switch'
import ct from 'countries-and-timezones';
import Combobox from "@/components/ui/Combobox.vue";

const props = defineProps<{
  timezones: Array<string>,
  timezone?: string,
  startDate?: string | null,
  endDate?: string | null,
  countryList: Record<string, { value: string, label: string }>,
  country?: string
}>();

const zone = ref<string | null>(props.timezone ?? null);
const start = ref<Date | null>(props.startDate ? new Date(Date.parse(props.startDate)) : null);
const end = ref<Date | null>(props.endDate ? new Date(Date.parse(props.endDate)) : null);
const hasNoDates = ref<boolean>(true);
const selectedCountry = ref<string | null>(props.country ?? null);

const dateFormater = (dateToFormat: string | null | Date) => {
  if (dateToFormat instanceof Date) {
      return dateToFormat.toISOString();
    } else if (typeof dateToFormat === "string") {
      return dateToFormat;
    }
  return null;
}

const formatedStartAt = computed(() => {
  return dateFormater(start.value);
})

const formatedEndAt = computed(() => {
  return dateFormater(end.value);
})

const countryOptions = computed(() => {
  return Object.values(props.countryList).map(country => ({
    id: country.value,
    text: country.label
  }));
});

const onTimezoneChange = (value: string) => {
  zone.value = value;
};

watch(selectedCountry, (newCountry) => {
  if (!newCountry || zone.value) return;
  const countryData = ct.getCountry(newCountry);
  if (!countryData) return;
  zone.value = countryData.timezones[0];
});

onMounted(() => {
  if (start.value || end.value) {
    hasNoDates.value = false;
  }
});
</script>
